import { Lesson } from '../types';

export const sampleLessons: Lesson[] = [
  {
    metadata: {
      lesson_id: "g1-counting-0-20-v2",
      title: "Counting Objects and Numbers to 20",
      subject: "Mathematics",
      grade: "1",
      language: "en",
      version: "2.0.0",
      duration_minutes: 30,
      created_at: "2025-08-18"
    },
    objectives: [
      "Count objects with 1-to-1 correspondence up to 20.",
      "Say, read, and write numbers 0–20 in order.",
      "Represent quantities on a ten-frame and match to numerals."
    ],
    success_criteria: [
      "I touch each object once while saying the next number.",
      "I can find a number on a 0–20 number line and name the numbers before and after it.",
      "I can fill a ten-frame to show a number and write the matching numeral."
    ],
    standards_alignment: [
      "CCSS.MATH.CONTENT.1.NBT.A.1",
      "CCSS.MATH.PRACTICE.MP1",
      "CCSS.MATH.PRACTICE.MP6"
    ],
    prerequisites: "Students can count to 10 and recognize numerals 0–10.",
    materials: {
      digital: [
        "interactive number line (0–20)",
        "interactive double ten-frame",
        "tap-to-count object set",
        "5-item auto-scored quick check"
      ],
      physical_alternatives: [
        "printed number line (0–20)",
        "two 5×2 ten-frames per student",
        "20 counters/bottle caps per student",
        "number cards 0–20",
        "pencils"
      ]
    },
    resources: {
      number_line_0_20: {
        type: "number_line",
        min: 0,
        max: 20,
        tick_every: 1,
        interactive: true
      },
      double_ten_frame: {
        type: "ten_frame",
        rows: 2,
        cols: 5,
        double_frame: true,
        drag_counters: true
      },
      tap_to_count: {
        type: "count_objects_canvas",
        max_objects: 20,
        randomize: true,
        touch_feedback: "ping"
      },
      timer_1: {
        type: "timer",
        duration: 60,
        auto_launch: false
      }
    },
    timeline: [
      {
        id: "warmup",
        name: "Warm-Up: Count & Move",
        minutes: 3,
        step_type: "warmup",
        teacher_script: [
          "Today we're counting up to 20. Stand up and count with me.",
          "Lead a clap-count 0–20, then whisper-count 10–0."
        ],
        student_actions: [
          "Clap and say numbers 0–20 in order.",
          "Whisper-count back from 10 to 0."
        ],
        checks_for_understanding: [
          {
            type: "quick_poll",
            prompt: "Show 7 with your fingers.",
            expected: "7 fingers"
          }
        ],
        resources: [
          { resourceId: "number_line_0_20" },
          { resourceId: "timer_1" },
          { resourceId: "tap_to_count" }
        ]
      },
      {
        id: "goal",
        name: "Learning Goal & Prior Knowledge",
        minutes: 3,
        step_type: "launch",
        teacher_script: [
          "Goal: Count objects and match to numbers 0–20.",
          "How do we avoid skipping or double-counting? (Touch—Say—Move.)",
          "Model counting 6 objects using one touch per object."
        ],
        student_actions: [
          "State the 1-to-1 rule (one touch per object).",
          "Choral count as teacher models."
        ],
        checks_for_understanding: [
          {
            type: "observation",
            look_for: "Students point once per object while counting aloud."
          }
        ],
        resources: [
          { resourceId: "tap_to_count" },
          { resourceId: "timer_1" }
        ]
      },
      {
        id: "teach",
        name: "Mini-Lesson: Ten-Frame & Number Line",
        minutes: 7,
        step_type: "direct_instruction",
        teacher_script: [
          "Ten-frames help us see numbers quickly. Fill top row left-to-right to 5, then continue.",
          "Show 13: fill 10, then add 3 more. Ask: How many full rows? How many more?",
          "On the number line, locate 13; ask for the numbers before (12) and after (14).",
          "Emphasize zero with an empty ten-frame."
        ],
        student_actions: [
          "Describe 13 as 'ten and three more'.",
          "Identify before/after numbers on the number line.",
          "Explain what 0 means (no counters)."
        ],
        checks_for_understanding: [
          {
            type: "cfu_question",
            prompt: "What comes after 17?",
            expected: "18"
          },
          { type: "thumbs", prompt: "Thumbs up if 13 is 10 and 3 more." }
        ],
        resources: [
          { resourceId: "double_ten_frame" },
          { resourceId: "number_line_0_20" },
          { resourceId: "timer_2" }
        ],
        teacher_moves: [
          {
            situation: "Student says 12 for 13",
            action: "Have student touch counters while recounting; restate 'ten and three more'."
          }
        ]
      }
    ],
    assessments: {
      quick_check_v1: {
        items: [
          {
            id: "QC1",
            type: "count_objects",
            stimulus: { objects: 8, layout: "random_clustered" },
            prompt: "How many?",
            answer: 8
          },
          {
            id: "QC2",
            type: "ten_frame_read",
            stimulus: { ten_frame: 12 },
            prompt: "What number does the ten-frame show?",
            answer: 12
          }
        ],
        scoring: { correct_per_item: 1, max: 5, mastery_threshold: 4 }
      }
    },
    misconceptions_and_responses: [
      {
        misconception: "Double-counting objects in clusters.",
        response: "Spread objects into a row; model Touch—Say—Move with a pointer."
      },
      {
        misconception: "Starting at 1 when set is empty.",
        response: "Show empty ten-frame and say, Zero means none."
      }
    ],
    differentiation: {
      supports: {
        verbal_count_frame: "Students say numbers while moving each counter into the ten-frame.",
        reduced_set: "Begin with 0–10, then extend to 20.",
        visuals: "Use high-contrast number line and bold numerals; larger touch targets."
      },
      extensions: {
        challenge: "Count on from 10 to reach 11–20 quickly; show 14 in two ways.",
        games: "Partner before/after flash with number cards."
      },
      ell: {
        sentence_frames: ["It is __.", "Before __ is __.", "After __ is __."],
        icons: ["touch", "say", "move"]
      }
    },
    classroom_management_notes: [
      "Seat partners side-by-side to share a device or counters.",
      "Use a visible countdown timer for each segment.",
      "Praise accurate 1-to-1 counting aloud."
    ],

    data_and_reporting: {
      events: [
        {
          when: "end_of_quick_check",
          capture: [
            "student_id",
            "score",
            "items_correct",
            "time_spent_seconds"
          ]
        }
      ],
      next_lesson_grouping_rule: "If score < 4, assign 'Build to 10' practice; else assign 'Count On from 10' practice."
    },
    offline_variant: {
      directions: [
        "Use printed number line and counters.",
        "Teacher calls numbers; students clip a clothespin to the number and fill ten-frame.",
        "Use a 5-item paper quick check; mark and sort for small groups."
      ]
    },
    home_connection: {
      family_note: "Count household items (spoons, buttons) up to 20. Ask: What comes after 9? Sketch a ten-frame to show 13."
    }
  },
  {
    metadata: {
      lesson_id: "g4-water-cycle-v1",
      title: "The Water Cycle",
      subject: "Science",
      grade: "4",
      language: "en",
      version: "1.0.0",
      duration_minutes: 45,
      created_at: "2025-08-18"
    },
    objectives: [
      "Understand the four main stages of the water cycle",
      "Explain how water moves through the environment",
      "Identify factors that affect the water cycle"
    ],
    success_criteria: [
      "I can name and describe the four stages of the water cycle",
      "I can explain how water moves from one stage to another",
      "I can identify human activities that affect the water cycle"
    ],
    standards_alignment: [
      "NGSS.4-ESS2-1",
      "CCSS.ELA-LITERACY.RI.4.3"
    ],
    prerequisites: "Students understand basic states of matter (solid, liquid, gas).",
    materials: {
      digital: [
        "interactive water cycle diagram",
        "virtual lab simulation",
        "digital assessment tools"
      ],
      physical_alternatives: [
        "water cycle diagram posters",
        "clear plastic containers",
        "water and ice",
        "heat source (lamp)",
        "science journals"
      ]
    },
    resources: {
      water_cycle_diagram: {
        type: "interactive_diagram",
        stages: ["evaporation", "condensation", "precipitation", "collection"],
        clickable: true,
        stage_data: {
          evaporation: {
            title: "Evaporation",
            description: "The process where liquid water turns into water vapor due to heat from the sun.",
            details: "Water molecules gain energy and move faster, breaking free from the liquid surface and becoming invisible water vapor in the air.",
            icon: "☀️"
          },
          condensation: {
            title: "Condensation", 
            description: "Water vapor cools and turns back into liquid water, forming clouds.",
            details: "As water vapor rises and cools, the molecules slow down and come together to form tiny water droplets that we see as clouds.",
            icon: "☁️"
          },
          precipitation: {
            title: "Precipitation",
            description: "Water falls from clouds as rain, snow, sleet, or hail.",
            details: "When water droplets in clouds become too heavy, they fall back to Earth's surface in various forms depending on temperature.",
            icon: "🌧️"
          },
          collection: {
            title: "Collection",
            description: "Water collects in oceans, lakes, rivers, and underground.",
            details: "Precipitation flows over and through the ground, eventually collecting in bodies of water where the cycle begins again.",
            icon: "🌊"
          }
        }
      },
      virtual_lab: {
        type: "simulation",
        components: ["water", "heat", "condensation_chamber"],
        interactive: true,
        component_info: {
          water: {
            icon: "💧",
            name: "Water",
            description: "Liquid water for the experiment"
          },
          heat: {
            icon: "🔥",
            name: "Heat Source",
            description: "Heat to evaporate water"
          },
          condensation_chamber: {
            icon: "❄️",
            name: "Condensation Chamber",
            description: "Cool area for water to condense"
          }
        },
        simulation_data: {
          title: "Mini Water Cycle Experiment",
          description: "Observe how water changes states through evaporation and condensation",
          initial_conditions: {
            temperature: 25,
            humidity: 60,
            water_level: 100
          },
          steps: [
            {
              step: 0,
              name: "Initial Setup",
              description: "Water at room temperature",
              conditions: {
                temperature: 25,
                humidity: 60,
                water_level: 100
              },
              status: "Ready to start experiment"
            },
            {
              step: 1,
              name: "Heating Phase",
              description: "Heat source warms the water",
              conditions: {
                temperature: 85,
                humidity: 75,
                water_level: 85
              },
              status: "Heating water... Water beginning to evaporate"
            },
            {
              step: 2,
              name: "Condensation Phase",
              description: "Water vapor cools and condenses",
              conditions: {
                temperature: 45,
                humidity: 95,
                water_level: 70
              },
              status: "Water vapor condensing on cool surface"
            }
          ],
          learning_objectives: [
            "Understand the three states of water (solid, liquid, gas)",
            "Observe the process of evaporation",
            "See how condensation forms water droplets",
            "Connect to the natural water cycle"
          ],
          safety_notes: [
            "This is a virtual experiment - no real heat sources used",
            "In real experiments, always use proper safety equipment",
            "Adult supervision required for real heat experiments"
          ]
        }
      },
      timer_2: {
        type: "timer",
        duration: 120,
        auto_launch: false
      },
      timer_3: {
        type: "timer",
        duration: 300,
        auto_launch: false
      }
    },
    timeline: [
      {
        id: "intro",
        name: "Introduction to Water Cycle",
        minutes: 8,
        step_type: "launch",
        teacher_script: [
          "Today we're learning about the water cycle - how water moves around our planet.",
          "Show the water cycle diagram and explain each stage.",
          "Ask students what they already know about rain, clouds, and water."
        ],
        student_actions: [
          "Observe the water cycle diagram",
          "Share prior knowledge about water in nature",
          "Ask questions about the cycle"
        ],
        checks_for_understanding: [
          {
            type: "observation",
            look_for: "Students can identify familiar parts of the water cycle"
          }
        ],
        resources: [
          { resourceId: "water_cycle_diagram" },
          { resourceId: "timer_2" }
        ]
      },
      {
        id: "experiment",
        name: "Mini Water Cycle Experiment",
        minutes: 20,
        step_type: "guided_practice",
        teacher_script: [
          "We'll create a mini water cycle in our classroom.",
          "Set up the containers and add water.",
          "Turn on the heat source and observe what happens."
        ],
        student_actions: [
          "Set up materials at group stations",
          "Add water to containers",
          "Record observations in science journals"
        ],
        checks_for_understanding: [
          {
            type: "partner_check",
            look_for: "Students accurately record their observations"
          }
        ],
        resources: [
          { resourceId: "virtual_lab" },
          { resourceId: "timer_3" }
        ]
      }
    ],
    assessments: {
      water_cycle_quiz: {
        items: [
          {
            id: "WC1",
            type: "multiple_choice",
            prompt: "Which stage of the water cycle turns liquid water into water vapor?",
            choices: [0, 1, 2, 3],
            answer: 0
          }
        ],
        scoring: { correct_per_item: 1, max: 5, mastery_threshold: 4 }
      }
    },
    misconceptions_and_responses: [
      {
        misconception: "Water disappears during evaporation",
        response: "Explain that water changes form but doesn't disappear - it becomes invisible water vapor."
      }
    ],
    differentiation: {
      supports: {
        visual_aids: "Use pictures and diagrams for each stage",
        hands_on: "Provide physical models to manipulate"
      },
      extensions: {
        research: "Research local water cycle patterns",
        presentation: "Create a water cycle presentation"
      },
      ell: {
        vocabulary: ["evaporation", "condensation", "precipitation", "collection"],
        visuals: ["Use pictures for each vocabulary word"]
      }
    },
    classroom_management_notes: [
      "Supervise carefully around heat sources",
      "Have students work in small groups",
      "Keep water containers away from electronics"
    ],

    data_and_reporting: {
      events: [
        {
          when: "experiment_complete",
          capture: ["student_id", "observations", "understanding_level"]
        }
      ],
      next_lesson_grouping_rule: "Based on experiment observations, group for follow-up activities."
    },
    offline_variant: {
      directions: [
        "Use printed diagrams and physical materials",
        "Teacher demonstrates the experiment",
        "Students draw and label the water cycle"
      ]
    },
    home_connection: {
      family_note: "Observe the water cycle in action: watch for rain, clouds, and puddles. Ask: Where does the water go?"
    }
  }
];
